/**
 * Vincent B963 (Eric 2026-02-14 SPEC STRICK PROD) — Page /dashboard/tags.
 *
 * Une vue autonome et centralisee pour tous les concepteurs / admins /
 * fondateurs :
 *   - Liste tous les tags de l'org avec count d'usage
 *   - Recherche + tri (count desc / alpha)
 *   - Multi-select via checkboxes
 *   - Bouton « Fusionner la selection » qui ouvre un mini-modal ou le
 *     user choisit la version canonique (radio + champ custom = B962)
 *
 * Complement de la page TagAnalyticsAdminPage (elle reste dediee a la
 * detection des paires quasi-identiques + historique). Cette page est
 * utile quand le user connait deja les tags qu'il veut fusionner.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { restoreScroll, scrollTop as _scrollTop, markModified } from "../lib/preserveScroll";
import { useAuth } from "../contexts/AuthContext";
import { findVariantGroups, TAG_TRANSFORMS, transformLabel, transformTitle } from "../lib/tagInflect";
import CsvImportModal from "../components/CsvImportModal"; // Tâche 1262

const API = process.env.REACT_APP_BACKEND_URL;

// Vincent B993 (Eric 2026-02-26 SPEC STRICK PROD) — Formatte une date
// ISO en format relatif court (« il y a 3h », « il y a 2j »). Utilisé
// pour afficher l'historique de renommage sous chaque tag sans exploser
// l'UI avec des timestamps complets.
function _formatRelative(isoStr, lang) {
  if (!isoStr) return "";
  try {
    const then = new Date(isoStr).getTime();
    if (!then) return "";
    const diffMs = Date.now() - then;
    const min = Math.round(diffMs / 60_000);
    if (min < 1) return lang === "fr" ? "à l'instant" : "just now";
    if (min < 60) return lang === "fr" ? `il y a ${min} min` : `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return lang === "fr" ? `il y a ${h} h` : `${h} h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return lang === "fr" ? `il y a ${d} j` : `${d} d ago`;
    const mo = Math.round(d / 30);
    if (mo < 12) return lang === "fr" ? `il y a ${mo} mois` : `${mo} mo ago`;
    const y = Math.round(mo / 12);
    return lang === "fr" ? `il y a ${y} an${y > 1 ? "s" : ""}` : `${y} y ago`;
  } catch {
    return "";
  }
}

// Vincent B1100 (Eric 2026-09-07) — `section` permet d'afficher UNE partie de
// la page dans un onglet dédié (au lieu de rediriger vers le bas de la page de
// fusion) :
//   "all"        → tout (page /dashboard/tags autonome)
//   "duplicates" → bannière doublons exacts + suggestions de fusion
//   "vocabulary" → acronymes + filtres + liste des tags (+ purge orphelins)
// `defaultOrphansOnly` pré-active le filtre orphelins pour l'onglet « Purger
// les orphelins ».
export default function TagsPage({ embedded = false, section = "all", defaultOrphansOnly = false }) {
  const showDuplicates = section === "all" || section === "duplicates";
  const showVocabulary = section === "all" || section === "vocabulary";
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const canEdit = !!(
    user &&
    (user.is_founder ||
      user.is_super_admin ||
      user.role === "admin" ||
      user.org_role === "concepteur" ||
      user.org_role === "admin" ||
      user.org_role === "founder" ||
      user.org_role === "ceo" ||
      user.org_role === "president" ||
      user.org_role === "vp")
  );

  const [tags, setTags] = useState([]);
  // Tâches 1262/1263 — Vocabulaire de l'organisation : tags importés sans
  // numéro de vidéo, plus la langue (Fr/En), le renvoi vers l'équivalent
  // français et le nombre. Un tag peut exister ici sans être sur une vidéo.
  const [vocabulary, setVocabulary] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  // Tâche 1268 — Un tag anglais sans renvoi est un cul-de-sac : on propose son
  // équivalent français (dictionnaire ou paire connue), sans rien imposer.
  const [bilingualSuggestions, setBilingualSuggestions] = useState([]);
  const [lastImportBatch, setLastImportBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("count"); // "count" | "alpha"
  // Vincent B992 (Eric 2026-02-26 SPEC STRICK PROD) — Filtre tags orphelins :
  // n'afficher que les tags utilisés 1 seule fois (candidats à purger ou
  // renommer vers un tag existant).
  const [orphansOnly, setOrphansOnly] = useState(defaultOrphansOnly);
  const [selected, setSelected] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeChoice, setMergeChoice] = useState("");
  const [merging, setMerging] = useState(false);
  // Vincent B993 (Eric 2026-02-26 SPEC STRICK PROD) — Historique de
  // renommage/fusion : consomme GET /tags/merge-history (B928). Indexé
  // par tag canonical pour affichage inline sous chaque ligne. Ignore
  // les entries rolled_back (déjà annulées → ne polluent pas l'UI).
  const [mergeHistory, setMergeHistory] = useState([]);
  // Vincent B997 (Eric 2026-02-26 SPEC STRICK PROD) — Doublons
  // orthographiques détectés (variantes de casse, d'accent, de tiret,
  // Levenshtein) via GET /tags/merge-nudges (B927). Paires dismissées
  // conservées en localStorage pour respecter le choix « Conserver ».
  const [nudges, setNudges] = useState([]);
  // Vincent B1032 (Eric 2026-02-26 SPEC STRICK PROD) — Groupes de doublons
  // EXACTS (fold-identiques) affichés en bannière rouge tout en haut avec
  // fusion d'un clic sur tout le groupe. Sous-ensemble strict des nudges
  // « variante_ponctuation », mais dédié pour une UX zéro-friction.
  const [exactDupGroups, setExactDupGroups] = useState([]);
  const [mergingGroupKey, setMergingGroupKey] = useState(null);
  const [dismissedPairs, setDismissedPairs] = useState(() => {
    try {
      const raw = localStorage.getItem("knoria.tags.dismissedPairs");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  // Vincent B1001 (Eric 2026-02-26 SPEC STRICK PROD) — Threshold
  // sémantique ajustable via slider (0.75..0.95). Persistance
  // localStorage. Refetch semantic-suggestions à chaque changement
  // (debounce 400ms pour ne pas spammer l'endpoint qui embed).
  const [semanticThreshold, setSemanticThreshold] = useState(() => {
    try {
      const raw = localStorage.getItem("knoria.tags.semanticThreshold");
      const n = raw ? parseFloat(raw) : 0.75;
      // Bogue 1193 — la borne haute passe à 1 (100 %), sinon un seuil de
      // 100 % mémorisé était rejeté au rechargement de la page.
      return Number.isFinite(n) && n >= 0.75 && n <= 1 ? n : 0.75;
    } catch { return 0.75; }
  });
  const [semanticLoading, setSemanticLoading] = useState(false);
  // Vincent B1009 (Eric 2026-02-26 SPEC STRICK PROD) — Historique
  // des purges du cache d'embeddings. Toggle du dropdown via
  // showEmbeddingsPurgeHistory. Fetched au clic (lazy).
  const [embeddingsPurgeHistory, setEmbeddingsPurgeHistory] = useState([]);
  const [showEmbeddingsPurgeHistory, setShowEmbeddingsPurgeHistory] = useState(false);
  const [loadingPurgeHistory, setLoadingPurgeHistory] = useState(false);
  const loadEmbeddingsPurgeHistory = async () => {
    setLoadingPurgeHistory(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/tags/embeddings-cache/history`,
        { withCredentials: true },
      );
      setEmbeddingsPurgeHistory(data?.purges || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingPurgeHistory(false);
    }
  };
  const toggleEmbeddingsPurgeHistory = async () => {
    const next = !showEmbeddingsPurgeHistory;
    setShowEmbeddingsPurgeHistory(next);
    if (next) await loadEmbeddingsPurgeHistory();
  };
  // Vincent B1010 (Eric 2026-02-26 SPEC STRICK PROD) — Acronymes
  // personnalisés de l'org. Section repliable dans TagsPage. Les
  // modifs re-déclenchent load() pour rafraîchir les suggestions.
  const [customAcronyms, setCustomAcronyms] = useState([]);
  const [showCustomAcronyms, setShowCustomAcronyms] = useState(false);
  const [newAcronym, setNewAcronym] = useState("");
  const [savingAcronym, setSavingAcronym] = useState(false);
  // Vincent B1014 (Eric 2026-02-26 SPEC STRICK PROD) — Candidats
  // acronymes détectés proactivement dans le vocab (tags courts non-
  // reconnus et fréquents). Affichés dans la même section « Acronymes
  // de l'organisation ».
  const [acronymCandidates, setAcronymCandidates] = useState([]);
  const loadCustomAcronyms = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/tags/custom-acronyms`,
        { withCredentials: true },
      );
      setCustomAcronyms(data?.acronyms || []);
    } catch { /* silent : section optionnelle */ }
    // Vincent B1014 — charge les candidats en parallèle. Endpoint séparé
    // (require_editor) pour ne pas ralentir le fetch principal.
    try {
      const { data } = await axios.get(
        `${API}/api/tags/acronym-candidates`,
        { withCredentials: true },
      );
      setAcronymCandidates(data?.candidates || []);
    } catch { /* silent : section optionnelle */ }
  };
  const addCustomAcronym = async (e) => {
    e?.preventDefault?.();
    const raw = (newAcronym || "").trim().toUpperCase();
    if (!raw) return;
    if (!/^[A-Z]{2,9}$/.test(raw)) {
      toast.error(lang === "fr"
        ? "Format invalide : 2 à 9 lettres majuscules ASCII (A-Z) uniquement."
        : "Invalid format: 2 to 9 uppercase ASCII letters (A-Z) only.");
      return;
    }
    setSavingAcronym(true);
    try {
      await axios.post(
        `${API}/api/tags/custom-acronyms`,
        { acronym: raw },
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? `« ${raw} » ajouté.` : `"${raw}" added.`);
      setNewAcronym("");
      await loadCustomAcronyms();
      await load();  // rafraîchit les suggestions acronym_case
    } catch (err) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (err?.response?.data?.detail || err.message));
    } finally {
      setSavingAcronym(false);
    }
  };
  const removeCustomAcronym = async (acr) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(lang === "fr"
      ? `Supprimer l'acronyme personnalisé « ${acr} » ?`
      : `Delete custom acronym "${acr}"?`)) return;
    try {
      await axios.delete(
        `${API}/api/tags/custom-acronyms/${encodeURIComponent(acr)}`,
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? `« ${acr} » supprimé.` : `"${acr}" removed.`);
      await loadCustomAcronyms();
      await load();
    } catch (err) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (err?.response?.data?.detail || err.message));
    }
  };
  // Vincent B1014 — accepte un candidat détecté : ajoute la version
  // MAJUSCULE aux acronymes custom de l'org (raccourci de l'ajout manuel).
  const acceptCandidate = async (candidate) => {
    const suggested = (candidate.suggested || candidate.tag || "").toUpperCase();
    if (!suggested) return;
    try {
      await axios.post(
        `${API}/api/tags/custom-acronyms`,
        { acronym: suggested },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `« ${suggested} » ajouté aux acronymes de l'organisation.`
        : `"${suggested}" added to organization acronyms.`);
      await loadCustomAcronyms();
      await load();
    } catch (err) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (err?.response?.data?.detail || err.message));
    }
  };
  // Vincent B1008 (Eric 2026-02-26 SPEC STRICK PROD) — Purge du cache
  // d'embeddings tags. Réservé aux admins (is_super_admin, is_founder,
  // role="admin"). Utile quand on change de modèle d'embedding (dim
  // ou distribution différente → scores incohérents). Confirmation
  // native `window.confirm` avant purge pour éviter les fat-finger.
  const isAdminForEmbeddingsCache = !!(
    user && (user.is_super_admin || user.is_founder || user.role === "admin")
  );
  const [clearingEmbeddingsCache, setClearingEmbeddingsCache] = useState(false);
  const clearEmbeddingsCache = async () => {
    const msg = lang === "fr"
      ? "Vider le cache d'embeddings tags ? Les prochaines suggestions sémantiques seront plus lentes (ré-embed complet)."
      : "Clear the tag embeddings cache? Next semantic suggestions will be slower (full re-embed).";
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setClearingEmbeddingsCache(true);
    try {
      const { data } = await axios.delete(
        `${API}/api/admin/tags/embeddings-cache`,
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `Cache vidé : ${data?.deleted_count ?? 0} entrée(s) supprimée(s).`
        : `Cache cleared: ${data?.deleted_count ?? 0} entry/entries removed.`);
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setClearingEmbeddingsCache(false);
    }
  };

  // Bogue 1192 (Éric 2026-06-25) — Toutes les actions de cette page
  // (renommer, fusionner, mettre en forme, unifier, purger…) se terminent par
  // `load()`. En conservant la position ICI, au point de passage unique, le
  // curseur reste au niveau du mot modifié quelle que soit l'action, au lieu
  // de sauter en haut de la liste parce que l'état de chargement a rétréci la
  // page.
  const load = async () => {
    const _scrollY = _scrollTop();
    setLoading(true);
    try {
      const [tagsRes, historyRes, nudgesRes, semanticRes, acronymRes, exactDupsRes] = await Promise.all([
        axios.get(`${API}/api/tags/org-vocabulary`, { withCredentials: true }),
        axios.get(`${API}/api/tags/merge-history`, { withCredentials: true }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/api/tags/merge-nudges?threshold=${semanticThreshold}`, { withCredentials: true }).catch(() => ({ data: { nudges: [] } })),
        // Vincent B999 (Eric 2026-02-26 SPEC STRICK PROD) — Suggestions
        // sémantiques via embeddings. Fusionnées dans le même panneau
        // que les nudges orthographiques (chaque item porte `reason` :
        // "variante_ponctuation" | "levenshtein" | "semantic").
        axios.get(`${API}/api/tags/semantic-suggestions?threshold=${semanticThreshold}`, { withCredentials: true }).catch(() => ({ data: { suggestions: [] } })),
        // Vincent B1007 (Eric 2026-02-26 SPEC STRICK PROD) — Suggestions
        // d'acronymisation passive : scan des tags qui matchent la liste
        // curée d'acronymes financiers sans variante MAJUSCULE existante.
        // Injectées dans le même panneau avec `reason="acronym_case"`.
        axios.get(`${API}/api/tags/acronym-suggestions`, { withCredentials: true }).catch(() => ({ data: { suggestions: [] } })),
        // Vincent B1032 (Eric 2026-02-26 SPEC STRICK PROD) — Doublons
        // exacts (fold-identiques) pour la bannière rouge en tête.
        axios.get(`${API}/api/tags/exact-duplicates`, { withCredentials: true }).catch(() => ({ data: { groups: [] } })),
      ]);
      setTags(tagsRes.data?.tags || []);
      setMergeHistory((historyRes.data?.history || []).filter((h) => !h.rolled_back));
      // Merge orthographic + semantic + acronym nudges. Le dédoublonnage
      // par pair_key est déjà fait côté backend (semantic exclut les
      // paires orthographiques ; acronym n'existe que si la MAJ n'existe
      // pas encore donc pas de collision).
      const orthoList = (nudgesRes.data?.nudges || []).map((n) => ({
        ...n,
        reason: n.reason || "orthographic",
      }));
      const semList = (semanticRes.data?.suggestions || []).map((s) => ({
        ...s,
        reason: "semantic",
      }));
      const acronymList = (acronymRes.data?.suggestions || []).map((s) => ({
        ...s,
        reason: "acronym_case",
      }));
      setNudges([...acronymList, ...orthoList, ...semList]);
      setExactDupGroups(exactDupsRes.data?.groups || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
      restoreScroll(_scrollY);
    }
  };
  useEffect(() => { load(); loadVocabulary(); loadBilingual(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Vincent B1010 — charge la liste des acronymes custom au mount
  // (indépendant de load() pour ne pas ralentir le fetch principal).
  useEffect(() => { loadCustomAcronyms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Vincent B1001 — Refetch semantic-suggestions quand le threshold
  // change (debounce 400ms + skip du 1er run car load() a déjà fetché).
  // Persistance du choix en localStorage.
  const _firstThresholdRun = useRef(true);
  useEffect(() => {
    try { localStorage.setItem("knoria.tags.semanticThreshold", String(semanticThreshold)); }
    catch { /* silent */ }
    if (_firstThresholdRun.current) {
      _firstThresholdRun.current = false;
      return;
    }
    const t = setTimeout(async () => {
      setSemanticLoading(true);
      try {
        // Bogue 1193 — Le seuil pilote les DEUX canaux : on rafraîchit donc
        // aussi les suggestions orthographiques, sinon « vente ← rente »
        // (0,80) restait affiché alors que l'utilisateur demande 100 %.
        const [sem, ortho] = await Promise.all([
          axios.get(`${API}/api/tags/semantic-suggestions?threshold=${semanticThreshold}`, { withCredentials: true }),
          axios.get(`${API}/api/tags/merge-nudges?threshold=${semanticThreshold}`, { withCredentials: true }),
        ]);
        const semList = (sem?.data?.suggestions || []).map((s) => ({ ...s, reason: "semantic" }));
        const orthoList = ortho?.data?.nudges || [];
        const ORTHO_REASONS = ["semantic", "variante_ponctuation", "quasi_identique", "levenshtein"];
        setNudges((prev) => [
          ...prev.filter((n) => !ORTHO_REASONS.includes(n.reason)),
          ...orthoList,
          ...semList,
        ]);
      } catch { /* silent */ }
      finally { setSemanticLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [semanticThreshold]);

  // Vincent B997 — Clé stable pour une paire (indépendante de l'ordre).
  const _pairKey = (a, b) => [String(a || "").toLowerCase(), String(b || "").toLowerCase()].sort().join("|");
  const visibleNudges = useMemo(
    () => nudges.filter((n) => !dismissedPairs.has(_pairKey(n.canonical, n.variant))),
    [nudges, dismissedPairs],
  );
  const _persistDismissed = (nextSet) => {
    setDismissedPairs(new Set(nextSet));
    try { localStorage.setItem("knoria.tags.dismissedPairs", JSON.stringify([...nextSet])); }
    catch { /* silent */ }
  };
  const acceptNudge = async (n) => {
    // Fusionne variant → canonical via l'endpoint B928 (rollback safe).
    try {
      await axios.post(
        `${API}/api/tags/apply-merge-nudge`,
        { canonical: n.canonical, variant: n.variant },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `« ${n.variant} » fusionné dans « ${n.canonical} ».`
        : `"${n.variant}" merged into "${n.canonical}".`);
      // Recharge tags + nudges (le nudge disparaît car variant n'existe plus).
      load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur fusion : " : "Merge error: ") + (e?.response?.data?.detail || e.message));
    }
  };
  const dismissNudge = (n) => {
    const key = _pairKey(n.canonical, n.variant);
    const next = new Set(dismissedPairs);
    next.add(key);
    _persistDismissed(next);
    // Bogue 1220 (Eric 2026-06-28) — Ce rejet ne vivait que dans le navigateur :
    // invisible pour les collègues, perdu au changement de poste, et absent du
    // journal des rejets. Il part maintenant aussi au serveur.
    axios.post(
      `${API}/api/tags/merge-nudges/reject`,
      { canonical: n.canonical, variant: n.variant, reason: n.reason || "" },
      { withCredentials: true },
    ).catch(() => { /* le confort local reste acquis même si l'appel échoue */ });
    toast.info(lang === "fr"
      ? `Suggestion conservée : « ${n.canonical} » et « ${n.variant} » resteront distincts.`
      : `Kept distinct: "${n.canonical}" and "${n.variant}" will not be merged.`);
  };
  // Vincent B1000 (Eric 2026-02-26 SPEC STRICK PROD) — Batch conserver :
  // ajoute toutes les paires visibles au Set dismissed en 1 clic.
  // Confirmation via window.confirm car l'action peut être perçue
  // comme radicale (« je ne veux plus voir aucune suggestion »).
  const dismissAllVisible = () => {
    if (visibleNudges.length === 0) return;
    const msg = lang === "fr"
      ? `Conserver définitivement les ${visibleNudges.length} paire(s) affichée(s) sans les fusionner ?\n\nEt masquer les suggestions du panneau.`
      : `Permanently keep the ${visibleNudges.length} displayed pair(s) without merging?\n\nWill hide suggestions from the panel.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    const next = new Set(dismissedPairs);
    for (const n of visibleNudges) {
      next.add(_pairKey(n.canonical, n.variant));
    }
    _persistDismissed(next);
    toast.success(lang === "fr"
      ? `${visibleNudges.length} suggestion(s) conservée(s). Panneau masqué.`
      : `${visibleNudges.length} suggestion(s) kept. Panel hidden.`);
  };

  // Vincent B992 — Compteur d'orphelins pour signal visuel dans le label.
  const orphansCount = useMemo(
    () => tags.filter((t) => (t.count || 0) === 1).length,
    [tags],
  );

  // B1128 — Groupes de graphies équivalentes (casse, accents, nombre).
  const variantGroups = useMemo(() => findVariantGroups(tags), [tags]);


  // Vincent B993 — Index history par tag canonical (jusqu'à 3 events
  // récents par tag pour ne pas exploser l'UI). Trié DESC par applied_at
  // (déjà l'ordre de retour du backend, on préserve).
  const historyByTag = useMemo(() => {
    const idx = {};
    for (const h of mergeHistory) {
      if (!h.canonical) continue;
      (idx[h.canonical] = idx[h.canonical] || []).push(h);
    }
    for (const k of Object.keys(idx)) {
      idx[k] = idx[k].slice(0, 3);
    }
    return idx;
  }, [mergeHistory]);

  const loadBilingual = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/tags/vocabulary/bilingual-suggestions`, { withCredentials: true });
      setBilingualSuggestions(data?.suggestions || []);
    } catch { /* suggestion : jamais bloquant */ }
  };

  const linkBilingual = async (tag, suggestion) => {
    try {
      const { data } = await axios.patch(
        `${API}/api/tags/vocabulary`,
        { tag, fr_equivalent: suggestion },
        { withCredentials: true },
      );
      setVocabulary((prev) => ({ ...prev, [tag]: data?.entry || prev[tag] }));
      setBilingualSuggestions((prev) => prev.filter((s) => s.tag !== tag));
      toast.success(lang === "fr"
        ? `« ${tag} » renvoie maintenant à « ${suggestion} ».`
        : `"${tag}" now points to "${suggestion}".`);
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const loadVocabulary = async () => {
    try {
      const { data } = await axios.get(`${API}/api/tags/vocabulary`, { withCredentials: true });
      const byTag = {};
      (data?.entries || []).forEach((e) => { byTag[e.tag] = e; });
      setVocabulary(byTag);
    } catch { /* le vocabulaire est un complément : son absence ne casse rien */ }
  };

  // Tâche 1263 — Bascule Fr/En d'un tag. Un tag anglais s'affiche en italique
  // avec un renvoi vers son équivalent français (facultatif, choix d'Eric).
  const setTagLang = async (tag, nextLang) => {
    try {
      const { data } = await axios.patch(
        `${API}/api/tags/vocabulary`,
        { tag, lang: nextLang },
        { withCredentials: true },
      );
      setVocabulary((prev) => ({ ...prev, [tag]: data?.entry || { tag, lang: nextLang } }));
      if (nextLang === "en" && !(data?.entry?.fr_equivalent || "").trim()) {
        toast.info(lang === "fr"
          ? `« ${tag} » est marqué anglais. Ajoute son équivalent français quand tu l'as.`
          : `"${tag}" is marked English. Add its French equivalent when you have it.`);
      }
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const setTagFrEquivalent = async (tag) => {
    const current = vocabulary[tag]?.fr_equivalent || "";
    // eslint-disable-next-line no-alert
    const next = window.prompt(
      lang === "fr" ? `Équivalent français de « ${tag} » :` : `French equivalent of "${tag}":`,
      current,
    );
    if (next === null) return;
    try {
      const { data } = await axios.patch(
        `${API}/api/tags/vocabulary`,
        { tag, fr_equivalent: next.trim() },
        { withCredentials: true },
      );
      setVocabulary((prev) => ({ ...prev, [tag]: data?.entry || prev[tag] }));
      toast.success(next.trim()
        ? (lang === "fr"
            ? `Renvoi enregistré : « ${tag} » → « ${next.trim()} ».`
            : `Link saved: "${tag}" → "${next.trim()}".`)
        : tr("Renvoi retiré.", "Link removed."));
    } catch (e) {
      toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Tâche 1262 — Les tags importés mais encore posés sur aucune vidéo
    // apparaissent dans la liste habituelle avec « 0 × », marqués
    // « vocabulaire » (choix d'Eric) : sinon on les aurait crus perdus.
    const used = new Set(tags.map((t) => t.tag));
    const vocabOnly = Object.values(vocabulary)
      .filter((v) => !used.has(v.tag))
      .map((v) => ({ tag: v.tag, count: 0, vocabulary_only: true }));
    let out = [...tags, ...vocabOnly];
    if (q) out = out.filter((t) => (t.tag || "").toLowerCase().includes(q));
    // Vincent B992 — Filtre orphelins actif → uniquement count === 1.
    if (orphansOnly) out = out.filter((t) => (t.count || 0) === 1);
    if (sortBy === "alpha") {
      out = [...out].sort((a, b) => (a.tag || "").localeCompare(b.tag || "", lang === "fr" ? "fr" : "en"));
    } else {
      out = [...out].sort((a, b) => (b.count || 0) - (a.count || 0) || (a.tag || "").localeCompare(b.tag || ""));
    }
    return out;
  }, [tags, vocabulary, query, sortBy, orphansOnly, lang]);

  const toggle = (tag) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((t) => t.tag));
    });
  };

  // Vincent B994 (Eric 2026-02-26 SPEC STRICK PROD) — Purger en batch
  // tous les tags orphelins sélectionnés. Sécurité :
  //   1. Filtre côté client : ne garde que les tags où count === 1
  //      (le backend refera sa propre vérif via l'endpoint).
  //   2. window.confirm avec le nombre exact de tags à purger.
  //   3. Toast Sonner avec bouton « Annuler » qui rollback en batch
  //      chaque merge_id créé par la purge (utilise l'endpoint existant
  //      /tags/merge-history/{id}/rollback livré par B928).
  const [purging, setPurging] = useState(false);

  // B1126 (Eric 2026-09-08) — Un seul chemin de suppression pour les trois
  // usages (purge des orphelins, effacement de la sélection, effacement d'un
  // tag) : l'endpoint retire le tag de TOUTES les vidéos et écrit l'historique
  // qui rend l'annulation possible. Trois implémentations séparées auraient
  // fini par en avoir une sans annulation.
  const purgeTagList = async (list, confirmMsg, successMsg) => {
    if (!list.length) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;
    setPurging(true);
    try {
      const { data } = await axios.post(
        `${API}/api/tags/purge-orphans`,
        { tags: list },
        { withCredentials: true },
      );
      const purgedIds = (data?.purged || []).map((p) => p.merge_id);
      const totalPurged = data?.total_purged || 0;
      toast.success(
        successMsg(totalPurged),
        {
          duration: 10000,
          action: purgedIds.length > 0
            ? {
                label: lang === "fr" ? "Annuler" : "Undo",
                onClick: async () => {
                  try {
                    // Vincent B996 (Eric 2026-02-26 SPEC STRICK PROD) —
                    // Collecte le `restored_count` de chaque rollback pour
                    // afficher un toast précis « X vidéos ont récupéré
                    // leurs tags » au lieu du générique « Purge annulée ».
                    // Les erreurs individuelles (rollback déjà appliqué,
                    // fusion introuvable) sont comptées dans `failed[]`
                    // sans bloquer les autres rollbacks du batch.
                    const results = await Promise.all(
                      purgedIds.map((mid) =>
                        axios.post(
                          `${API}/api/tags/merge-history/${mid}/rollback`,
                          {},
                          { withCredentials: true },
                        )
                          .then((r) => ({ ok: true, data: r.data }))
                          .catch((err) => ({ ok: false, err })),
                      ),
                    );
                    const succeeded = results.filter((r) => r.ok);
                    const failed = results.filter((r) => !r.ok);
                    const totalVideosRestored = succeeded.reduce(
                      (acc, r) => acc + (r.data?.restored_count || 0),
                      0,
                    );
                    const tagsRestored = succeeded.length;
                    if (tagsRestored === 0) {
                      toast.error(
                        lang === "fr"
                          ? "Aucun tag n'a pu être restauré."
                          : "No tag could be restored.",
                      );
                    } else {
                      const tagLabel = lang === "fr"
                        ? `${tagsRestored} tag${tagsRestored > 1 ? "s" : ""}`
                        : `${tagsRestored} tag${tagsRestored > 1 ? "s" : ""}`;
                      const vidLabel = lang === "fr"
                        ? `${totalVideosRestored} vidéo${totalVideosRestored > 1 ? "s" : ""}`
                        : `${totalVideosRestored} video${totalVideosRestored > 1 ? "s" : ""}`;
                      const failedSuffix = failed.length > 0
                        ? (lang === "fr"
                            ? ` · ${failed.length} rollback(s) échoué(s)`
                            : ` · ${failed.length} rollback(s) failed`)
                        : "";
                      toast.success(
                        lang === "fr"
                          ? `Purge annulée : ${tagLabel} restauré${tagsRestored > 1 ? "s" : ""} sur ${vidLabel}${failedSuffix}.`
                          : `Purge undone: ${tagLabel} restored on ${vidLabel}${failedSuffix}.`,
                        { duration: 6000 },
                      );
                    }
                    load();
                  } catch (e) {
                    toast.error((lang === "fr" ? "Erreur annulation : " : "Undo error: ") + (e?.message || ""));
                  }
                },
              }
            : undefined,
        },
      );
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur purge : " : "Purge error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  // B1126 — Applique une mise en forme à UN tag via le renommage serveur
  // existant (`apply-merge-nudge`) : si le nom d'arrivée existe déjà, les deux
  // tags fusionnent, ce qui est exactement le comportement voulu.
  // Bogue 1197 — Message de fusion avec bouton « Annuler » (10 s), branché sur
  // le rollback existant. Si l'API n'a pas renvoyé d'identifiant de fusion, on
  // retombe sur un simple message de succès plutôt que sur un bouton mort.
  const undoableMergeToast = (message, mergeId, variant) => {
    if (!mergeId) {
      toast.success(message);
      return;
    }
    toast.success(message, {
      duration: 10000,
      action: {
        label: lang === "fr" ? "Annuler" : "Undo",
        onClick: async () => {
          try {
            await axios.post(
              `${API}/api/tags/merge-history/${mergeId}/rollback`,
              {},
              { withCredentials: true },
            );
            toast.success(lang === "fr"
              ? `Fusion annulée — « ${variant} » est rétabli.`
              : `Merge undone — "${variant}" is back.`);
            markModified(`tags-page-row-${variant}`);
            await load();
          } catch (e) {
            toast.error((lang === "fr" ? "Annulation impossible : " : "Undo failed: ")
              + (e?.response?.data?.detail || e.message));
          }
        },
      },
    });
  };

  const transformTag = async (tag, kind) => {
    const tr = TAG_TRANSFORMS[kind];
    if (!tr) return;
    const next = tr.apply(tag);
    if (next === tag) {
      toast.info(lang === "fr" ? `« ${tag} » est déjà ainsi.` : `"${tag}" is already like that.`);
      return;
    }
    const existing = tags.find((x) => x.tag === next && x.tag !== tag);
    const msg = lang === "fr"
      ? `Renommer « ${tag} » en « ${next} » ?${existing ? `\n\n« ${next} » existe déjà (${existing.count}×) : les deux tags seront fusionnés.` : ""}`
      : `Rename "${tag}" to "${next}"?${existing ? `\n\n"${next}" already exists (${existing.count}×): both tags will be merged.` : ""}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setPurging(true);
    try {
      const { data: mergeRes } = await axios.post(
        `${API}/api/tags/apply-merge-nudge`,
        { canonical: next, variant: tag },
        { withCredentials: true },
      );
      // Bogue 1197 (Éric 2026-06-25) — « Annuler » directement dans le message
      // de confirmation : une fusion faite par erreur se répare en un clic,
      // sans passer par l'historique. L'API garde déjà de quoi revenir en
      // arrière (`tag_merge_history`), on l'expose juste au bon moment.
      undoableMergeToast(
        lang === "fr" ? `« ${tag} » → « ${next} »` : `"${tag}" → "${next}"`,
        mergeRes?.merge_id,
        tag,
      );
      setSelected(new Set());
      // Bogue 1194 — le tag conservé s'illumine une seconde après le rechargement.
      markModified(`tags-page-row-${next}`);
      await load();
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  // B1127 — Applique une mise en forme à TOUS les tags cochés. Les tags
  // déjà conformes sont ignorés (et annoncés) : les inclure ferait autant
  // d'appels serveur inutiles, et un « 0 modifié » incompréhensible.
  const transformSelection = async (kind) => {
    const tr = TAG_TRANSFORMS[kind];
    if (!tr) return;
    const list = [...selected];
    const changes = list
      .map((tag) => ({ from: tag, to: tr.apply(tag) }))
      .filter((c) => c.to !== c.from);
    if (changes.length === 0) {
      toast.info(lang === "fr"
        ? `Les ${list.length} tag(s) cochés sont déjà ainsi.`
        : `The ${list.length} checked tag(s) are already like that.`);
      return;
    }
    const preview = changes.slice(0, 8).map((c) => `« ${c.from} » → « ${c.to} »`).join("\n")
      + (changes.length > 8 ? "\n…" : "");
    const skipped = list.length - changes.length;
    const msg = lang === "fr"
      ? `Appliquer « ${transformLabel(tr, lang)} » à ${changes.length} tag(s) ?\n\n${preview}${skipped ? `\n\n${skipped} tag(s) déjà conformes seront ignorés.` : ""}`
      : `Apply "${transformLabel(tr, lang)}" to ${changes.length} tag(s)?\n\n${preview}${skipped ? `\n\n${skipped} already-compliant tag(s) will be skipped.` : ""}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setPurging(true);
    let done = 0;
    let failed = 0;
    try {
      for (const c of changes) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(
            `${API}/api/tags/apply-merge-nudge`,
            { canonical: c.to, variant: c.from },
            { withCredentials: true },
          );
          done += 1;
        } catch {
          // Un échec ne doit pas abandonner les 39 tags suivants.
          failed += 1;
        }
      }
      if (done) {
        toast.success(lang === "fr"
          ? `${done} tag(s) mis en forme${failed ? `, ${failed} en échec` : ""}.`
          : `${done} tag(s) formatted${failed ? `, ${failed} failed` : ""}.`);
      } else {
        toast.error(lang === "fr" ? "Aucun tag n'a pu être mis en forme." : "No tag could be formatted.");
      }
      setSelected(new Set());
      await load();
    } finally {
      setPurging(false);
    }
  };

  // B1128 (Eric 2026-09-08) — Unifie un groupe de graphies vers la variante
  // choisie. Chaque variante est renommée vers la canonique : le serveur
  // fusionne, donc aucune vidéo ne perd son tag.
  const unifyGroup = async (group, canonical) => {
    const others = group.variants.filter((v) => v.tag !== canonical);
    const msg = lang === "fr"
      ? `Unifier ${others.length + 1} graphies vers « ${canonical} » ?\n\n${others.map((v) => `« ${v.tag} » (${v.count}×)`).join("\n")}\n\nLes vidéos concernées porteront « ${canonical} ».`
      : `Unify ${others.length + 1} spellings into "${canonical}"?\n\n${others.map((v) => `"${v.tag}" (${v.count}×)`).join("\n")}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setPurging(true);
    let done = 0;
    try {
      for (const v of others) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(
            `${API}/api/tags/apply-merge-nudge`,
            { canonical, variant: v.tag },
            { withCredentials: true },
          );
          done += 1;
        } catch {
          /* on continue : les autres variantes doivent être unifiées quand même */
        }
      }
      toast.success(lang === "fr"
        ? `${done} graphie(s) unifiée(s) vers « ${canonical} ».`
        : `${done} spelling(s) unified into "${canonical}".`);
      setSelected(new Set());
      markModified(`tags-page-row-${canonical}`);
      await load();
    } finally {
      setPurging(false);
    }
  };

  const _preview = (list) =>
    list.slice(0, 5).join(", ") + (list.length > 5 ? "…" : "");

  const doPurgeOrphans = async () => {
    const orphanSelected = [...selected].filter((tag) => {
      const t = tags.find((x) => x.tag === tag);
      return t && (t.count || 0) === 1;
    });
    if (orphanSelected.length === 0) {
      toast.error(lang === "fr"
        ? "Aucun tag orphelin dans la sélection."
        : "No orphan tag in the selection.");
      return;
    }
    await purgeTagList(
      orphanSelected,
      lang === "fr"
        ? `Purger définitivement ${orphanSelected.length} tag(s) orphelin(s) ?\n\n${_preview(orphanSelected)}\n\nUne annulation sera possible pendant quelques secondes via un toast.`
        : `Permanently purge ${orphanSelected.length} orphan tag(s)?\n\n${_preview(orphanSelected)}\n\nAn undo will be available for a few seconds via a toast.`,
      (n) => (lang === "fr" ? `${n} tag(s) orphelin(s) purgé(s).` : `${n} orphan tag(s) purged.`),
    );
  };

  // B1126 — « Effacer » EFFACE. Avant, ce bouton ne faisait que vider la
  // sélection : Eric cliquait « Effacer » et rien ne disparaissait jamais.
  // Le bouton qui déselectionne s'appelle maintenant « Désélectionner ».
  const doDeleteSelected = async () => {
    const list = [...selected];
    if (list.length === 0) {
      toast.error(lang === "fr" ? "Aucun tag sélectionné." : "No tag selected.");
      return;
    }
    const used = list.reduce((acc, tag) => {
      const t = tags.find((x) => x.tag === tag);
      return acc + ((t?.count || 0) > 1 ? 1 : 0);
    }, 0);
    const warn = used > 0
      ? (lang === "fr"
          ? `\n\nAttention : ${used} de ces tags sont utilisés sur plusieurs vidéos.`
          : `\n\nWarning: ${used} of these tags are used on several videos.`)
      : "";
    await purgeTagList(
      list,
      lang === "fr"
        ? `Effacer définitivement ${list.length} tag(s) ?\n\n${_preview(list)}${warn}\n\nIls seront retirés de toutes les vidéos. Une annulation sera possible pendant quelques secondes via un toast.`
        : `Permanently delete ${list.length} tag(s)?\n\n${_preview(list)}${warn}\n\nThey will be removed from all videos. An undo will be available for a few seconds via a toast.`,
      (n) => (lang === "fr" ? `${n} tag(s) effacé(s).` : `${n} tag(s) deleted.`),
    );
  };

  const doDeleteOne = async (tag, count) => {
    await purgeTagList(
      [tag],
      lang === "fr"
        ? `Effacer le tag « ${tag} » ?\n\nIl sera retiré de ${count || 0} vidéo(s). Une annulation sera possible pendant quelques secondes via un toast.`
        : `Delete tag "${tag}"?\n\nIt will be removed from ${count || 0} video(s). An undo will be available for a few seconds via a toast.`,
      () => (lang === "fr" ? `« ${tag} » effacé.` : `"${tag}" deleted.`),
    );
  };

  const openMergeModal = () => {
    if (selected.size < 2) {
      toast.error(lang === "fr" ? "Sélectionne au moins 2 tags pour fusionner." : "Select at least 2 tags to merge.");
      return;
    }
    // Pre-selection : le tag le plus utilise dans la selection.
    const chosen = [...selected]
      .map((tag) => tags.find((t) => t.tag === tag))
      .filter(Boolean)
      .sort((a, b) => (b.count || 0) - (a.count || 0))[0];
    setMergeChoice(chosen?.tag || "");
    setShowMergeModal(true);
  };

  const doMerge = async () => {
    const finalCanonical = String(mergeChoice || "").trim();
    if (!finalCanonical) {
      toast.error(lang === "fr" ? "Choisis ou tape une version." : "Choose or type a version.");
      return;
    }
    const variants = [...selected].filter((v) => v !== finalCanonical);
    if (variants.length === 0) {
      // Vincent B991 (Eric 2026-02-26 SPEC STRICK PROD) — En mode renommer
      // (1 tag sélectionné + custom name === le tag lui-même), il n'y a
      // rien à migrer. On tolère et on ferme le modal sans erreur.
      toast.info(lang === "fr"
        ? "Aucun changement (la version canonique est identique au tag sélectionné)."
        : "No change (canonical version is identical to the selected tag).");
      setShowMergeModal(false);
      return;
    }
    setMerging(true);
    let successCount = 0;
    for (const variant of variants) {
      try {
        await axios.post(
          `${API}/api/tags/apply-merge-nudge`,
          { canonical: finalCanonical, variant },
          { withCredentials: true },
        );
        successCount += 1;
      } catch (e) {
        toast.error(`« ${variant} » : ${e?.response?.data?.detail || e.message}`);
      }
    }
    setMerging(false);
    setShowMergeModal(false);
    setSelected(new Set());
    toast.success(
      lang === "fr"
        ? `Fusion appliquee : ${successCount}/${variants.length} variantes migrees vers « ${finalCanonical} ».`
        : `Merge applied: ${successCount}/${variants.length} variants migrated to "${finalCanonical}".`,
    );
    markModified(`tags-page-row-${finalCanonical}`);
    await load();
  };

  if (!canEdit) {
    return (
      <div className="p-6" data-testid="tags-page-no-permission">
        <h1 className="text-2xl font-semibold text-zinc-900">
          {lang === "fr" ? "Accès refusé" : "Access denied"}
        </h1>
        <p className="text-sm text-zinc-600 mt-2">
          {lang === "fr"
            ? "Cette page est réservée aux concepteurs / admins / fondateurs."
            : "This page is restricted to designers / admins / founders."}
        </p>
      </div>
    );
  }

  // Vincent B991 (Eric 2026-02-26 SPEC STRICK PROD) — Mode `embedded`
  // pour intégration inline dans SearchResultsPage. En mode embed :
  //   - Wrapper compact (pas de max-w-5xl, pas de padding externe).
  //   - Le H1 « Tags » n'est pas rendu (le parent a déjà son titre).
  const wrapperCls = embedded ? "" : "p-6 max-w-5xl mx-auto";

  return (
    <div className={wrapperCls} data-testid="tags-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-zinc-900">
            {lang === "fr" ? "Tags" : "Tags"}
          </h1>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-[#002FA7] hover:underline disabled:opacity-50"
            data-testid="tags-page-refresh-btn"
          >
            {lang === "fr" ? "Rafraîchir" : "Refresh"}
          </button>
        </div>
      )}

      {/* Tâche 1268 — Paires bilingues proposées : un clic pour relier. */}
      {canEdit && bilingualSuggestions.length > 0 && (
        <div
          className="mb-4 rounded-md border border-violet-300 bg-violet-50 px-3 py-2"
          data-testid="tags-page-bilingual-panel"
        >
          <p className="text-sm font-semibold text-violet-900 mb-2">
            {tr("Tags anglais sans équivalent français", "English tags with no French equivalent")}
            <span
              className="ml-2 px-1.5 py-0.5 rounded-full bg-violet-200 text-violet-900 text-[10px] font-bold tabular-nums"
              data-testid="tags-page-bilingual-count"
            >
              {bilingualSuggestions.length}
            </span>
          </p>
          <ul className="flex flex-col gap-1" data-testid="tags-page-bilingual-list">
            {bilingualSuggestions.map((s, i) => (
              <li
                key={s.tag}
                className="flex items-center gap-2 flex-wrap text-xs bg-white border border-violet-200 rounded px-2 py-1.5"
                data-testid={`tags-page-bilingual-row-${i}`}
              >
                <span className="italic font-semibold text-zinc-900">{s.tag}</span>
                <span className="text-violet-800">→</span>
                <span className="font-semibold text-zinc-900">{s.suggestion}</span>
                <span className="text-[11px] text-zinc-500">{tr("source : ", "source: ")}{s.source}</span>
                <button
                  type="button"
                  onClick={() => linkBilingual(s.tag, s.suggestion)}
                  className="ml-auto text-[11px] font-semibold px-2 py-1 rounded bg-[#002FA7] text-white hover:bg-[#001f7a]"
                  data-testid={`tags-page-bilingual-link-${i}`}
                  title={tr("Enregistre le renvoi vers l'équivalent français", "Saves the link to the French equivalent")}
                >
                  {tr("Relier", "Link")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tâche 1262 (Eric 2026-06-28) — Importer une LISTE de tags, sans
          numéro de vidéo : le vocabulaire se pose d'abord, les vidéos
          viennent après. */}
      {canEdit && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="tags-page-import-row">
          <button
            onClick={() => setShowImportModal(true)}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#002FA7] text-white hover:bg-[#001f7a]"
            data-testid="tags-page-import-btn"
            title={tr("Importer une liste de tags (CSV ou Excel). Aucun numéro de vidéo n'est demandé.", "Import a tag list (CSV or Excel). No video number is asked for.")}
          >
            {lang === "fr" ? "Importer des tags" : "Import tags"}
          </button>
          <span className="text-[11px] text-zinc-500">
            {/* Tâche 1429 — Ces mots sont des NOMS DE COLONNES du fichier,
                pas de la prose : les laisser en français dans la version
                anglaise laissait croire qu'il fallait écrire « langue » dans
                son fichier. Le lecteur du serveur accepte les deux jeux
                d'en-têtes (`_LANG_HEADERS`, `_FR_HEADERS`, `_NUMBER_HEADERS`
                dans `routers/tag_vocabulary.py`), on annonce donc les noms
                anglais en anglais. */}
            {lang === "fr"
              ? "Une colonne de tags suffit. Colonnes facultatives : langue, equivalent_fr, nombre."
              : "A single tag column is enough. Optional columns: language, equivalent_fr, number."}
          </span>
          {lastImportBatch && (
            <button
              onClick={async () => {
                try {
                  const { data } = await axios.post(
                    `${API}/api/tags/vocabulary/rollback/${lastImportBatch}`,
                    {},
                    { withCredentials: true },
                  );
                  toast.success(lang === "fr"
                    ? `Import annulé : ${data?.removed || 0} tag(s) retiré(s).`
                    : `Import cancelled: ${data?.removed || 0} tag(s) removed.`);
                  setLastImportBatch(null);
                  loadVocabulary();
                } catch (e) {
                  toast.error(tr("Erreur : ", "Error: ") + (e?.response?.data?.detail || e.message));
                }
              }}
              className="text-[11px] px-2 py-1 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50"
              data-testid="tags-page-import-rollback-btn"
            >
              {lang === "fr" ? "Annuler le dernier import" : "Undo last import"}
            </button>
          )}
        </div>
      )}

      {/* Vincent B1032 (Eric 2026-02-26 SPEC STRICK PROD) — Bannière
          rouge saillante des doublons EXACTS (fold-identiques : casse,
          accents, ponctuation). Placée en TOUT PREMIER pour maximiser
          la visibilité, avec fusion d'un clic sur le groupe entier
          (canonical + tous ses variants d'un coup). Sous-ensemble strict
          des nudges orthographiques mais UX zéro-friction : pas de
          slider, pas de dismiss, juste « Fusionner ». Rendu uniquement
          si ≥ 1 groupe. */}
      {showDuplicates && exactDupGroups.length > 0 && (
        <div
          className="mb-4 border-2 border-red-400 bg-red-50 rounded-lg p-3"
          data-testid="tags-page-exact-duplicates-banner"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-bold text-red-900">
              {lang === "fr" ? "⚠ Doublons exacts détectés" : "⚠ Exact duplicates detected"}
            </span>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full bg-red-200 text-red-900 font-bold"
              data-testid="tags-page-exact-duplicates-count"
            >
              {exactDupGroups.length}
            </span>
            <span className="text-[11px] text-red-800/90">
              {lang === "fr"
                ? "Même forme normalisée (casse / accents / ponctuation différents). Fusionner en un clic pour éviter la fragmentation du vocabulaire."
                : "Same normalized form (casing / accents / punctuation differ). Merge in one click to avoid fragmenting the vocabulary."}
            </span>
          </div>
          <ul className="space-y-1.5">
            {exactDupGroups.map((g) => {
              const groupKey = [g.canonical, ...g.variants.map((v) => v.tag)].join("|");
              const isMerging = mergingGroupKey === groupKey;
              return (
                <li
                  key={groupKey}
                  className="flex items-center gap-2 flex-wrap text-xs bg-white rounded-md px-2 py-1.5 border border-red-200"
                  data-testid={`tags-page-exact-duplicate-group-${g.canonical}`}
                >
                  <span className="inline-flex items-center gap-1">
                    <span className="font-semibold text-emerald-700" title={lang === "fr" ? "Conservé" : "Kept"}>
                      « {g.canonical} »
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      ({g.canonical_count})
                    </span>
                  </span>
                  <span className="text-zinc-400">←</span>
                  <span className="inline-flex items-center gap-1 flex-wrap">
                    {g.variants.map((v) => (
                      <span
                        key={v.tag}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-mono line-through decoration-red-400"
                        title={lang === "fr" ? `Fusionné dans « ${g.canonical} »` : `Merged into "${g.canonical}"`}
                      >
                        {v.tag}
                        <span className="text-[9px] text-red-700 not-italic no-underline">
                          ({v.count})
                        </span>
                      </span>
                    ))}
                  </span>
                  <button
                    type="button"
                    onClick={async () => {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm(
                        lang === "fr"
                          ? `Fusionner ${g.variants.length} variant(s) dans « ${g.canonical} » ? ${g.total_count} vidéo(s) au total.`
                          : `Merge ${g.variants.length} variant(s) into "${g.canonical}"? ${g.total_count} video(s) total.`,
                      )) return;
                      setMergingGroupKey(groupKey);
                      try {
                        const { data: r } = await axios.post(
                          `${API}/api/tags/merge-exact-duplicate-group`,
                          { canonical: g.canonical, variants: g.variants.map((v) => v.tag) },
                          { withCredentials: true },
                        );
                        toast.success(
                          lang === "fr"
                            ? `${r.merged_variants} variant(s) fusionné(s) — ${r.videos_updated} vidéo(s) mise(s) à jour.`
                            : `${r.merged_variants} variant(s) merged — ${r.videos_updated} video(s) updated.`,
                        );
                        markModified(`tags-page-row-${g.canonical}`);
                        await load();
                      } catch (e) {
                        toast.error(
                          (lang === "fr" ? "Erreur : " : "Error: ") +
                            (e?.response?.data?.detail || e.message),
                        );
                      } finally {
                        setMergingGroupKey(null);
                      }
                    }}
                    disabled={isMerging}
                    className="ml-auto px-2.5 py-1 rounded-md bg-red-600 text-white text-[11px] font-semibold hover:bg-red-500 disabled:opacity-50"
                    data-testid={`tags-page-exact-duplicate-merge-btn-${g.canonical}`}
                    title={lang === "fr"
                      ? `Fusionner ${g.variants.length} variant(s) dans « ${g.canonical} »`
                      : `Merge ${g.variants.length} variant(s) into "${g.canonical}"`}
                  >
                    {isMerging
                      ? (lang === "fr" ? "…" : "…")
                      : (lang === "fr" ? "Fusionner d'un clic" : "Merge in one click")}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Vincent B997 (Eric 2026-02-26 SPEC STRICK PROD) — Détection
          des tags similaires (variantes orthographiques). Rendu
          seulement si nudges > 0 et non-dismissed. Chaque ligne :
          canonical (majoritaire) ← variant (à fusionner) + 2 boutons.
          Vincent B1001 (Eric 2026-02-26) — Le panneau reste visible
          même à 0 paire pour laisser Vincent baisser le threshold
          sémantique via le slider (sinon il ne peut pas expérimenter). */}
      {showDuplicates && tags.length > 0 && (
        <div
          className="mb-4 border border-amber-300 bg-amber-50 rounded-lg p-3"
          data-testid="tags-page-nudges-panel"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-semibold text-amber-900">
              {lang === "fr" ? "🔍 Suggestions de fusion" : "🔍 Merge suggestions"}
            </span>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 font-bold"
              data-testid="tags-page-nudges-count"
            >
              {visibleNudges.length}
            </span>
            <span className="text-[11px] text-amber-800/80">
              {lang === "fr"
                ? "Doublons orthographiques + proches sémantiquement (embeddings)."
                : "Spelling duplicates + semantically close (embeddings)."}
            </span>
            {/* Vincent B1001 (Eric 2026-02-26 SPEC STRICK PROD) — Slider
                de threshold sémantique. Plus haut = suggestions plus
                strictes (moins de faux positifs), plus bas = plus
                permissif (proximité conceptuelle plus large). */}
            <label
              className="inline-flex items-center gap-1.5 text-[11px] text-amber-900 font-medium ml-2"
              data-testid="tags-page-nudges-threshold-label"
              title={lang === "fr"
                ? "Seuil de similarité (75% = permissif, 100% = uniquement les tags de sens strictement identique, à la casse/accent/tiret près). Bogue 1193 : le seuil pilote aussi les suggestions orthographiques."
                : "Similarity threshold (75% = loose, 100% = only strictly identical meanings, up to case/accents/hyphen). Bug 1193: the threshold now also drives spelling suggestions."}
            >
              <span>{lang === "fr" ? "Seuil sémantique" : "Semantic threshold"}</span>
              <input
                type="range"
                min="0.75"
                max="1"
                step="0.01"
                value={semanticThreshold}
                onChange={(e) => setSemanticThreshold(parseFloat(e.target.value))}
                className="w-24 accent-violet-600"
                data-testid="tags-page-nudges-threshold-slider"
                aria-label={lang === "fr" ? "Ajuster le seuil de similarité sémantique" : "Adjust semantic similarity threshold"}
              />
              <span className="font-mono text-[10px] w-10" data-testid="tags-page-nudges-threshold-value">
                {Math.round(semanticThreshold * 100)}%
              </span>
              {semanticLoading && (
                <span className="text-[10px] text-violet-700 italic" data-testid="tags-page-nudges-threshold-loading">
                  {lang === "fr" ? "…" : "…"}
                </span>
              )}
            </label>
            {/* Vincent B1008 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
                admin pour vider le cache d'embeddings tags. Placé à côté
                du slider sémantique car c'est le même cluster de
                fonctionnalités (embeddings). Visible uniquement pour les
                admins pour éviter des purges accidentelles.
                Vincent B1009 (Eric 2026-02-26) — Bouton toggle
                « Historique » à côté du bouton purge pour consulter
                l'audit des purges passées (qui, quand, combien). */}
            {isAdminForEmbeddingsCache && (
              <span className="inline-flex items-center gap-1 relative" data-testid="tags-page-embeddings-cache-controls">
                <button
                  type="button"
                  onClick={clearEmbeddingsCache}
                  disabled={clearingEmbeddingsCache}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-white border border-violet-300 text-violet-800 hover:bg-violet-50 disabled:opacity-50"
                  data-testid="tags-page-clear-embeddings-cache-btn"
                  title={lang === "fr"
                    ? "Vider le cache d'embeddings tags (utile après un changement de modèle)"
                    : "Clear the tag embeddings cache (useful after switching embedding model)"}
                >
                  {clearingEmbeddingsCache
                    ? (lang === "fr" ? "Purge…" : "Clearing…")
                    : (lang === "fr" ? "Vider cache embeddings" : "Clear embeddings cache")}
                </button>
                <button
                  type="button"
                  onClick={toggleEmbeddingsPurgeHistory}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-white border border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="tags-page-embeddings-purge-history-toggle"
                  aria-expanded={showEmbeddingsPurgeHistory}
                  title={lang === "fr"
                    ? "Voir l'historique des purges du cache d'embeddings"
                    : "See embeddings cache purge history"}
                >
                  {showEmbeddingsPurgeHistory
                    ? (lang === "fr" ? "Masquer historique" : "Hide history")
                    : (lang === "fr" ? "Historique" : "History")}
                </button>
                {showEmbeddingsPurgeHistory && (
                  <div
                    className="absolute top-full right-0 mt-1 z-20 w-80 max-h-72 overflow-y-auto bg-white border border-violet-300 rounded-lg shadow-lg p-2"
                    data-testid="tags-page-embeddings-purge-history-panel"
                    role="region"
                    aria-label={lang === "fr" ? "Historique des purges" : "Purge history"}
                  >
                    <div className="text-xs font-semibold text-violet-900 mb-1 flex items-center justify-between">
                      <span>{lang === "fr" ? "Purges du cache embeddings" : "Embeddings cache purges"}</span>
                      <span
                        className="text-[10px] font-mono text-violet-600"
                        data-testid="tags-page-embeddings-purge-history-count"
                      >
                        {embeddingsPurgeHistory.length}
                      </span>
                    </div>
                    {loadingPurgeHistory && (
                      <p className="text-[11px] italic text-zinc-500" data-testid="tags-page-embeddings-purge-history-loading">
                        {lang === "fr" ? "Chargement…" : "Loading…"}
                      </p>
                    )}
                    {!loadingPurgeHistory && embeddingsPurgeHistory.length === 0 && (
                      <p className="text-[11px] italic text-zinc-500" data-testid="tags-page-embeddings-purge-history-empty">
                        {lang === "fr" ? "Aucune purge enregistrée." : "No purge recorded yet."}
                      </p>
                    )}
                    <ul className="flex flex-col gap-1" data-testid="tags-page-embeddings-purge-history-list">
                      {embeddingsPurgeHistory.map((p) => (
                        <li
                          key={p.id}
                          className="text-[11px] text-zinc-800 border border-zinc-200 rounded px-2 py-1"
                          data-testid={`tags-page-embeddings-purge-history-item-${p.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono truncate" title={p.admin_email || ""}>
                              {p.admin_name || p.admin_email || "?"}
                            </span>
                            <span className="text-[10px] font-mono text-emerald-700">
                              −{p.deleted_count ?? 0}
                            </span>
                          </div>
                          <div className="text-[10px] text-zinc-500 font-mono">
                            {p.purged_at ? new Date(p.purged_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "?"}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </span>
            )}
            {/* Vincent B1000 (Eric 2026-02-26 SPEC STRICK PROD) — Batch
                conserver : masque toutes les suggestions restantes en 1
                clic quand le vocabulaire est jugé propre. */}
            {visibleNudges.length > 0 && (
              <button
                type="button"
                onClick={dismissAllVisible}
                className="ml-auto text-xs font-semibold px-2 py-1 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                data-testid="tags-page-nudges-dismiss-all-btn"
                title={lang === "fr"
                  ? "Conserver toutes les paires affichées sans les fusionner et masquer le panneau."
                  : "Keep all displayed pairs without merging and hide the panel."}
              >
                {lang === "fr" ? "Tout conserver" : "Keep all"}
              </button>
            )}
          </div>
          {visibleNudges.length === 0 && (
            <p
              className="text-xs italic text-amber-800/70 px-1"
              data-testid="tags-page-nudges-empty"
            >
              {lang === "fr"
                ? `Aucun doublon détecté à ${Math.round(semanticThreshold * 100)}%. Baissez le seuil pour explorer les paires moins proches.`
                : `No duplicate at ${Math.round(semanticThreshold * 100)}%. Lower the threshold to explore looser pairs.`}
            </p>
          )}
          <ul className="flex flex-col gap-1.5" data-testid="tags-page-nudges-list">
            {visibleNudges.map((n) => {
              const key = _pairKey(n.canonical, n.variant);
              return (
                <li
                  key={key}
                  className="flex items-center gap-2 text-sm bg-white/70 rounded px-2 py-1.5"
                  data-testid={`tags-page-nudge-row-${key}`}
                >
                  <span className="font-semibold text-zinc-900 truncate">{n.canonical}</span>
                  {n.canonical_count > 0 && (
                    <span className="text-xs text-zinc-500 font-mono">({n.canonical_count}×)</span>
                  )}
                  {n.reason === "acronym_case" && (
                    <span
                      className="text-[10px] text-emerald-700 italic"
                      data-testid={`tags-page-nudge-new-canonical-${key}`}
                      title={lang === "fr" ? "Nouvelle forme MAJUSCULE canonique" : "New UPPERCASE canonical form"}
                    >
                      {lang === "fr" ? "(nouveau)" : "(new)"}
                    </span>
                  )}
                  <span className="text-zinc-400">←</span>
                  <span className="text-zinc-700 truncate line-through decoration-amber-500/60">{n.variant}</span>
                  <span className="text-xs text-zinc-500 font-mono">({n.variant_count}×)</span>
                  <span className="text-[10px] text-zinc-500 ml-1">
                    {Math.round((n.similarity || 0) * 100)}%
                  </span>
                  {/* Vincent B999 — Badge type de suggestion (orthographic
                      vs semantic) pour aider Vincent à décider. */}
                  <span
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      n.reason === "semantic"
                        ? "bg-violet-100 text-violet-800"
                        : n.reason === "acronym_case"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-sky-100 text-sky-800"
                    }`}
                    data-testid={`tags-page-nudge-reason-${key}`}
                    title={n.reason === "semantic"
                      ? (lang === "fr" ? "Proche sémantiquement (embeddings)" : "Semantically close (embeddings)")
                      : n.reason === "acronym_case"
                      ? (lang === "fr" ? "Acronyme connu à mettre en MAJUSCULES" : "Known acronym to UPPERCASE")
                      : (lang === "fr" ? "Variante orthographique (casse/accent/tiret)" : "Spelling variant (case/accent/hyphen)")}
                  >
                    {n.reason === "semantic"
                      ? (lang === "fr" ? "sémantique" : "semantic")
                      : n.reason === "acronym_case"
                      ? (lang === "fr" ? "acronyme" : "acronym")
                      : (lang === "fr" ? "orthographe" : "spelling")}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => acceptNudge(n)}
                      className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`tags-page-nudge-merge-btn-${key}`}
                      title={n.reason === "acronym_case"
                        ? (lang === "fr"
                          ? `Acronymiser « ${n.variant} » en « ${n.canonical} »`
                          : `Uppercase "${n.variant}" to "${n.canonical}"`)
                        : (lang === "fr"
                          ? `Fusionner « ${n.variant} » dans « ${n.canonical} »`
                          : `Merge "${n.variant}" into "${n.canonical}"`)}
                    >
                      {n.reason === "acronym_case"
                        ? (lang === "fr" ? "Acronymiser" : "Uppercase")
                        : (lang === "fr" ? "Fusionner" : "Merge")}
                    </button>
                    <button
                      type="button"
                      onClick={() => dismissNudge(n)}
                      className="text-xs font-semibold px-2 py-1 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      data-testid={`tags-page-nudge-keep-btn-${key}`}
                      title={lang === "fr"
                        ? "Ne plus proposer cette fusion (garder les 2 tags distincts)"
                        : "Stop suggesting this merge (keep the 2 tags distinct)"}
                    >
                      {lang === "fr" ? "Conserver" : "Keep"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Vincent B1010 (Eric 2026-02-26 SPEC STRICK PROD) — Section
          repliable pour gérer les acronymes personnalisés de l'org.
          Visible pour les éditeurs/admins (`canEdit`). Toggle-only :
          replié par défaut pour ne pas polluer la vue courante. */}
      {showVocabulary && canEdit && (
        <details
          className="mb-4 border border-emerald-300 bg-emerald-50 rounded-lg"
          data-testid="tags-page-custom-acronyms-section"
          open={showCustomAcronyms}
          onToggle={(e) => setShowCustomAcronyms(e.currentTarget.open)}
        >
          <summary
            className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-emerald-900 flex items-center gap-2"
            data-testid="tags-page-custom-acronyms-toggle"
          >
            <span>{lang === "fr" ? "🔤 Acronymes de l'organisation" : "🔤 Organization acronyms"}</span>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-200 text-emerald-900 font-bold"
              data-testid="tags-page-custom-acronyms-count"
            >
              {customAcronyms.length}
            </span>
            <span className="text-[11px] font-normal text-emerald-800/80 ml-1">
              {lang === "fr"
                ? "Ajoute tes acronymes internes (ex : « KUS », « GAAP ») pour compléter les suggestions."
                : "Add your internal acronyms (e.g. \"KUS\", \"GAAP\") to enrich the suggestions."}
            </span>
          </summary>
          <div className="px-3 pb-3">
            <form
              onSubmit={addCustomAcronym}
              className="flex items-center gap-2 mb-2"
              data-testid="tags-page-custom-acronyms-form"
            >
              <input
                type="text"
                value={newAcronym}
                onChange={(e) => setNewAcronym(e.target.value.toUpperCase())}
                placeholder={lang === "fr" ? "Ex : GAAP" : "e.g. GAAP"}
                pattern="[A-Za-z]{2,9}"
                maxLength={9}
                className="flex-1 max-w-xs px-2 py-1.5 border border-emerald-300 rounded-md text-sm font-mono uppercase"
                data-testid="tags-page-custom-acronyms-input"
                aria-label={lang === "fr" ? "Nouvel acronyme" : "New acronym"}
              />
              <button
                type="submit"
                disabled={savingAcronym || !newAcronym}
                className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                data-testid="tags-page-custom-acronyms-add-btn"
              >
                {savingAcronym
                  ? (lang === "fr" ? "Ajout…" : "Adding…")
                  : (lang === "fr" ? "Ajouter" : "Add")}
              </button>
              <span className="text-[10px] text-emerald-800/80 italic">
                {lang === "fr" ? "2 à 9 lettres MAJUSCULES ASCII." : "2 to 9 UPPERCASE ASCII letters."}
              </span>
            </form>
            {customAcronyms.length === 0 ? (
              <p
                className="text-xs italic text-emerald-800/70"
                data-testid="tags-page-custom-acronyms-empty"
              >
                {lang === "fr"
                  ? "Aucun acronyme personnalisé pour l'instant. Les suggestions reposent uniquement sur la liste standard."
                  : "No custom acronyms yet. Suggestions rely on the standard list only."}
              </p>
            ) : (
              <ul
                className="flex flex-wrap gap-1.5"
                data-testid="tags-page-custom-acronyms-list"
              >
                {customAcronyms.map((a) => (
                  <li
                    key={a.id || a.acronym}
                    className="inline-flex items-center gap-1 bg-white border border-emerald-300 rounded-full pl-2 pr-1 py-0.5 text-xs font-mono"
                    data-testid={`tags-page-custom-acronym-chip-${a.acronym}`}
                    title={a.added_by_email
                      ? (lang === "fr" ? `Ajouté par ${a.added_by_email}` : `Added by ${a.added_by_email}`)
                      : ""}
                  >
                    <span className="font-semibold text-emerald-900">{a.acronym}</span>
                    <button
                      type="button"
                      onClick={() => removeCustomAcronym(a.acronym)}
                      className="w-4 h-4 rounded-full bg-emerald-100 hover:bg-red-200 text-emerald-800 hover:text-red-800 text-[10px] leading-none"
                      data-testid={`tags-page-custom-acronym-remove-${a.acronym}`}
                      aria-label={lang === "fr" ? `Supprimer ${a.acronym}` : `Remove ${a.acronym}`}
                      title={lang === "fr" ? "Supprimer" : "Remove"}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {/* Vincent B1014 (Eric 2026-02-26 SPEC STRICK PROD) —
                Candidats détectés proactivement (tags courts fréquents
                non-reconnus). Affichés en sous-section avec bouton
                « Ajouter » qui raccourcit vers le POST custom-acronyms. */}
            {acronymCandidates.length > 0 && (
              <div
                className="mt-3 border-t border-emerald-200 pt-2"
                data-testid="tags-page-acronym-candidates"
              >
                <p className="text-xs font-semibold text-emerald-900 mb-1">
                  {lang === "fr"
                    ? `🔍 Candidats détectés (${acronymCandidates.length})`
                    : `🔍 Detected candidates (${acronymCandidates.length})`}
                  <span className="ml-2 font-normal text-emerald-800/80 text-[11px]">
                    {lang === "fr"
                      ? "Tags courts fréquents non-reconnus : peut-être des acronymes internes ?"
                      : "Short frequent unrecognized tags: possibly internal acronyms?"}
                  </span>
                </p>
                <ul
                  className="flex flex-wrap gap-1.5"
                  data-testid="tags-page-acronym-candidates-list"
                >
                  {acronymCandidates.map((c) => (
                    <li
                      key={c.suggested}
                      className="inline-flex items-center gap-1 bg-white border border-amber-300 rounded-full pl-2 pr-1 py-0.5 text-xs font-mono"
                      data-testid={`tags-page-acronym-candidate-${c.suggested}`}
                      title={lang === "fr"
                        ? `Utilisé ${c.count} fois dans tes vidéos comme « ${c.tag} »`
                        : `Used ${c.count} times in your videos as "${c.tag}"`}
                    >
                      <span className="font-semibold text-amber-900">{c.suggested}</span>
                      <span className="text-[10px] text-amber-700/80">×{c.count}</span>
                      <button
                        type="button"
                        onClick={() => acceptCandidate(c)}
                        className="w-4 h-4 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-[10px] leading-none"
                        data-testid={`tags-page-acronym-candidate-accept-${c.suggested}`}
                        aria-label={lang === "fr" ? `Ajouter ${c.suggested} comme acronyme` : `Add ${c.suggested} as acronym`}
                        title={lang === "fr" ? "Ajouter comme acronyme personnalisé" : "Add as custom acronym"}
                      >
                        +
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}

      {showVocabulary && (
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={lang === "fr" ? "Rechercher un tag…" : "Search a tag…"}
          className="flex-1 min-w-[200px] px-3 py-2 border border-zinc-300 rounded-md text-sm"
          data-testid="tags-page-search-input"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-zinc-300 rounded-md text-sm"
          data-testid="tags-page-sort-select"
        >
          <option value="count">{lang === "fr" ? "Tri : utilisation" : "Sort: usage"}</option>
          <option value="alpha">{lang === "fr" ? "Tri : A→Z" : "Sort: A→Z"}</option>
        </select>
        {/* Vincent B992 (Eric 2026-02-26 SPEC STRICK PROD) — Filtre
            orphelins : uniquement les tags utilisés 1× (candidats à
            purger ou renommer vers un tag existant). Compteur affiché
            dans le label pour donner un signal immédiat. */}
        <label
          className={`inline-flex items-center gap-2 text-xs px-2.5 py-2 rounded-md border cursor-pointer select-none transition-colors ${
            orphansOnly
              ? "bg-amber-50 border-amber-300 text-amber-900 font-semibold"
              : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid="tags-page-orphans-filter-label"
        >
          <input
            type="checkbox"
            checked={orphansOnly}
            onChange={(e) => setOrphansOnly(e.target.checked)}
            className="h-3.5 w-3.5 accent-amber-600"
            data-testid="tags-page-orphans-filter"
          />
          <span>
            {lang === "fr" ? "Orphelins" : "Orphans"}
            <span
              className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold tabular-nums"
              data-testid="tags-page-orphans-count"
            >
              {orphansCount}
            </span>
          </span>
        </label>
      </div>
      )}

      {/* B1128 (Eric 2026-09-08) — Deux graphies d'un même mot coupent le
          vocabulaire en deux : une recherche sur l'une ne trouve pas les
          vidéos de l'autre, et personne ne s'en aperçoit. On les signale
          AVANT la liste, avec la variante la plus utilisée proposée d'office
          (c'est la forme que l'équipe a réellement adoptée). */}
      {showVocabulary && variantGroups.length > 0 && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
          data-testid="tags-page-variant-groups"
        >
          <p className="text-xs font-semibold text-amber-900">
            {lang === "fr"
              ? `${variantGroups.length} mot(s) écrit(s) de plusieurs façons`
              : `${variantGroups.length} word(s) written several ways`}
          </p>
          <ul className="mt-1.5 space-y-1">
            {variantGroups.slice(0, 8).map((g) => (
              <li key={g.key} className="flex flex-wrap items-center gap-2 text-xs"
                  data-testid={`tags-page-variant-group-${g.key}`}>
                <span className="text-amber-900">
                  {g.variants.map((v) => `« ${v.tag} » (${v.count}×)`).join(" · ")}
                </span>
                <select
                  defaultValue={g.canonical}
                  onChange={(e) => unifyGroup(g, e.target.value)}
                  disabled={purging}
                  className="text-[11px] border border-amber-300 rounded px-1 py-0.5 bg-white"
                  data-testid={`tags-page-variant-choice-${g.key}`}
                  title={lang === "fr"
                    ? "Choisir la graphie à conserver — l'unification part au choix"
                    : "Pick the spelling to keep — unification starts on choice"}
                >
                  {g.variants.map((v) => (
                    <option key={v.tag} value={v.tag}>{v.tag}</option>
                  ))}
                </select>
                <button
                  onClick={() => unifyGroup(g, g.canonical)}
                  disabled={purging}
                  className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  data-testid={`tags-page-unify-${g.key}`}
                  title={lang === "fr"
                    ? `Tout renommer en « ${g.canonical} » (la graphie la plus utilisée)`
                    : `Rename everything to "${g.canonical}" (most used spelling)`}
                >
                  {lang === "fr" ? `Unifier en « ${g.canonical} »` : `Unify to "${g.canonical}"`}
                </button>
              </li>
            ))}
          </ul>
          {variantGroups.length > 8 && (
            <p className="mt-1 text-[11px] text-amber-800">
              {lang === "fr"
                ? `… et ${variantGroups.length - 8} autre(s). Traitez ceux-ci d'abord : la liste se met à jour.`
                : `… and ${variantGroups.length - 8} more.`}
            </p>
          )}
        </div>
      )}

      {showVocabulary && selected.size > 0 && (
        <div
          className="mb-3 flex items-center justify-between px-3 py-2 bg-teal-50 border border-teal-200 rounded-md"
          data-testid="tags-page-selection-bar"
        >
          <span className="text-xs text-teal-900">
            {selected.size}{" "}
            {lang === "fr" ? "sélectionné(s)" : "selected"}
          </span>
          {/* B1127 (Eric 2026-09-08) — Mise en forme de TOUTE la sélection :
              corriger 40 tags un par un n'est pas un travail, c'est une
              punition. Un seul aperçu, une seule confirmation. */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="tags-page-bulk-format">
            <span className="text-[11px] text-teal-900 mr-1">
              {lang === "fr" ? "Tout mettre en forme :" : "Format all:"}
            </span>
            {Object.entries(TAG_TRANSFORMS).map(([key, tr]) => (
              <button
                key={key}
                onClick={() => transformSelection(key)}
                disabled={purging || selected.size === 0}
                className="text-[10px] px-1.5 py-0.5 rounded border border-teal-300 bg-white text-teal-900 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40"
                data-testid={`tags-page-bulk-transform-${key}`}
                title={lang === "fr"
                  ? `${transformTitle(tr, lang)} — appliqué aux ${selected.size} tag(s) cochés`
                  : `${transformTitle(tr, lang)} — applied to the ${selected.size} checked tag(s)`}
              >
                {transformLabel(tr, lang)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {/* B1126 (Eric 2026-09-08) — Ces deux boutons étaient CONFONDUS :
                « Effacer » ne faisait que vider la sélection, donc rien ne
                disparaissait jamais. Le nom dit maintenant ce que chacun fait. */}
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs px-2 py-1 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
              data-testid="tags-page-clear-selection"
              title={lang === "fr"
                ? "Décoche tout, sans rien supprimer"
                : "Uncheck everything, deletes nothing"}
            >
              {lang === "fr" ? "Désélectionner" : "Deselect"}
            </button>
            <button
              onClick={doDeleteSelected}
              disabled={purging || selected.size === 0}
              className="text-xs font-semibold px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="tags-page-delete-selected"
              title={lang === "fr"
                ? `Efface les ${selected.size} tag(s) cochés de toutes les vidéos (annulable via toast)`
                : `Delete the ${selected.size} checked tag(s) from all videos (undoable via toast)`}
            >
              {purging
                ? (lang === "fr" ? "Effacement…" : "Deleting…")
                : (lang === "fr" ? `Effacer (${selected.size})` : `Delete (${selected.size})`)}
            </button>
            <button
              onClick={openMergeModal}
              disabled={selected.size < 1}
              className="text-xs font-semibold px-3 py-1 rounded bg-[#002FA7] text-white hover:bg-[#001f70] disabled:opacity-50"
              data-testid="tags-page-merge-btn"
              title={selected.size === 1
                ? (lang === "fr" ? "Renommer ce tag partout où il est utilisé" : "Rename this tag everywhere")
                : (lang === "fr" ? "Fusionner les tags sélectionnés en une version canonique" : "Merge selected tags into a canonical version")}
            >
              {selected.size === 1
                ? (lang === "fr" ? "Renommer" : "Rename")
                : (lang === "fr" ? "Fusionner la sélection" : "Merge selection")}
            </button>
            {/* Vincent B994 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
                bulk purge : n'apparaît que quand au moins 1 tag orphelin
                (count===1) est sélectionné. Confirmation + toast annulable. */}
            {[...selected].some((tag) => {
              const t = tags.find((x) => x.tag === tag);
              return t && (t.count || 0) === 1;
            }) && (
              <button
                onClick={doPurgeOrphans}
                disabled={purging}
                className="text-xs font-semibold px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="tags-page-purge-orphans-btn"
                title={lang === "fr"
                  ? "Supprime tous les tags orphelins (utilisés 1×) de la sélection. Annulable via toast."
                  : "Delete all orphan tags (used 1×) from the selection. Undoable via toast."}
              >
                {purging
                  ? (lang === "fr" ? "Purge…" : "Purging…")
                  : (lang === "fr" ? "Purger les orphelins" : "Purge orphans")}
              </button>
            )}
          </div>
        </div>
      )}

      {showVocabulary && (loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-zinc-500" data-testid="tags-page-empty">
          {query
            ? (lang === "fr" ? `Aucun tag ne correspond à « ${query} »` : `No tag matches "${query}"`)
            : (lang === "fr" ? "Aucun tag dans l'organisation." : "No tags in the organization.")}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2 text-xs text-zinc-600">
            <input
              type="checkbox"
              checked={selected.size > 0 && selected.size === filtered.length}
              onChange={toggleAll}
              data-testid="tags-page-select-all"
            />
            <span className="flex-1">
              {lang === "fr" ? `${filtered.length} tag(s)` : `${filtered.length} tag(s)`}
            </span>
            <span className="w-16 text-right">
              {lang === "fr" ? "Usage" : "Usage"}
            </span>
          </div>
          <ul className="divide-y divide-zinc-100" data-testid="tags-page-list">
            {filtered.map((t) => {
              const isSel = selected.has(t.tag);
              // Vincent B993 — Historique de renommage/fusion sous chaque tag.
              const events = historyByTag[t.tag] || [];
              const meta = vocabulary[t.tag];
              return (
                <li
                  key={t.tag}
                  className={`flex flex-col gap-1 px-3 py-2 cursor-pointer text-sm ${isSel ? "bg-teal-50" : "hover:bg-zinc-50"}`}
                  onClick={() => toggle(t.tag)}
                  data-testid={`tags-page-row-${t.tag}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(t.tag)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`tags-page-checkbox-${t.tag}`}
                    />
                    <span className={`flex-1 truncate ${meta?.lang === "en" ? "italic" : ""}`}>
                      {t.tag}
                      {meta?.lang === "en" && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setTagFrEquivalent(t.tag); }}
                          className="ml-2 not-italic text-[11px] text-[#002FA7] hover:underline"
                          data-testid={`tags-page-fr-equivalent-${t.tag}`}
                          title={meta?.fr_equivalent
                            ? `Équivalent français : « ${meta.fr_equivalent} » — cliquer pour modifier`
                            : "Aucun équivalent français encore : cliquer pour l'ajouter"}
                        >
                          {meta?.fr_equivalent
                            ? `→ ${meta.fr_equivalent}`
                            : tr("→ équivalent fr ?", "→ fr equivalent?")}
                        </button>
                      )}
                      {t.vocabulary_only && (
                        <span
                          className="ml-2 not-italic text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200"
                          data-testid={`tags-page-vocab-badge-${t.tag}`}
                          title={tr("Tag du vocabulaire importé : encore posé sur aucune vidéo.", "Imported vocabulary tag: not attached to any video yet.")}
                        >
                          {lang === "fr" ? "vocabulaire" : "vocabulary"}
                        </span>
                      )}
                    </span>
                    <span className="w-16 text-right text-xs text-zinc-500">{t.count}×</span>
                    {/* Tâche 1263 — Langue du tag : « Fr » ou « En ». Un tag
                        anglais est un alias : il s'affiche en italique avec un
                        renvoi vers son équivalent français. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTagLang(t.tag, meta?.lang === "en" ? "fr" : "en");
                      }}
                      className={`text-[10px] font-bold w-7 py-0.5 rounded border ${
                        meta?.lang === "en"
                          ? "bg-violet-100 text-violet-900 border-violet-300"
                          : "bg-white text-zinc-600 border-zinc-300 hover:border-[#002FA7]"
                      }`}
                      data-testid={`tags-page-lang-toggle-${t.tag}`}
                      title={meta?.lang === "en"
                        ? "Tag anglais (alias). Cliquer pour le repasser en français."
                        : "Tag français. Cliquer pour le marquer comme alias anglais."}
                    >
                      {meta?.lang === "en" ? "En" : "Fr"}
                    </button>
                    {/* Vincent B991 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
                        renommer inline : sélectionne ce tag seul et ouvre le
                        modal renommer/fusionner. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(new Set([t.tag]));
                        setMergeChoice(t.tag);
                        setShowMergeModal(true);
                      }}
                      className="ml-1 text-xs text-zinc-500 hover:text-[#002FA7] px-1.5 py-0.5 rounded hover:bg-white"
                      data-testid={`tags-page-rename-btn-${t.tag}`}
                      title={lang === "fr" ? "Renommer ce tag" : "Rename this tag"}
                      aria-label={lang === "fr" ? `Renommer ${t.tag}` : `Rename ${t.tag}`}
                    >
                      ✎
                    </button>
                    {/* B1126 (Eric 2026-09-08) — Mise en forme en un clic.
                        Avant : ouvrir « Renommer », retaper le tag à la main,
                        valider — pour un simple accent de casse ou un « s ».
                        Le bouton est DÉSACTIVÉ quand la transformation ne
                        changerait rien : un clic sans effet ferait croire à
                        une panne. */}
                    {Object.entries(TAG_TRANSFORMS).map(([key, tr]) => {
                      // Tâche 1263 — « singulier » et « pluriel » sont rendus
                      // juste après, COLLÉS : ce sont deux faces du même
                      // réglage, les séparer les faisait passer pour deux
                      // actions sans rapport.
                      if (key === "singular" || key === "plural") return null;
                      const next = tr.apply(t.tag);
                      const noop = next === t.tag;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); transformTag(t.tag, key); }}
                          disabled={noop || purging}
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${
                            noop
                              ? "border-zinc-200 text-zinc-300 cursor-not-allowed"
                              : "border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] bg-white"
                          }`}
                          data-testid={`tags-page-transform-${key}-${t.tag}`}
                          title={noop
                            ? (lang === "fr"
                              ? `${transformTitle(tr, lang)} — déjà le cas pour « ${t.tag} »`
                              : `${transformTitle(tr, lang)} — already the case for “${t.tag}”`)
                            : (lang === "fr"
                              ? `${transformTitle(tr, lang)} : « ${t.tag} » → « ${next} »`
                              : `${transformTitle(tr, lang)}: “${t.tag}” → “${next}”`)}
                        >
                          {transformLabel(tr, lang)}
                        </button>
                      );
                    })}
                    <span className="inline-flex" data-testid={`tags-page-number-group-${t.tag}`}>
                      {["singular", "plural"].map((key, i) => {
                        const tr = TAG_TRANSFORMS[key];
                        const next = tr.apply(t.tag);
                        const noop = next === t.tag;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); transformTag(t.tag, key); }}
                            disabled={noop || purging}
                            className={`text-[10px] px-1.5 py-0.5 border ${
                              i === 0 ? "rounded-l" : "rounded-r -ml-px"
                            } ${
                              noop
                                ? "border-zinc-200 text-zinc-300 cursor-not-allowed"
                                : "border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] bg-white"
                            }`}
                            data-testid={`tags-page-transform-${key}-${t.tag}`}
                            title={noop
                              ? (lang === "fr"
                                ? `${transformTitle(tr, lang)} — déjà le cas pour « ${t.tag} »`
                                : `${transformTitle(tr, lang)} — already the case for “${t.tag}”`)
                              : (lang === "fr"
                                ? `${transformTitle(tr, lang)} : « ${t.tag} » → « ${next} »`
                                : `${transformTitle(tr, lang)}: “${t.tag}” → “${next}”`)}
                          >
                            {transformLabel(tr, lang)}
                          </button>
                        );
                      })}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); doDeleteOne(t.tag, t.count); }}
                      disabled={purging}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                      data-testid={`tags-page-delete-${t.tag}`}
                      title={lang === "fr"
                        ? `Efface « ${t.tag} » de ${t.count || 0} vidéo(s) (annulable via toast)`
                        : `Delete "${t.tag}" from ${t.count || 0} video(s) (undoable via toast)`}
                    >
                      {lang === "fr" ? "effacer" : "delete"}
                    </button>
                  </div>
                  {events.length > 0 && (
                    <ul
                      className="ml-6 flex flex-col gap-0.5 text-[11px] text-zinc-500"
                      data-testid={`tags-page-history-${t.tag}`}
                    >
                      {events.map((ev) => (
                        <li
                          key={ev.id}
                          className="truncate"
                          data-testid={`tags-page-history-row-${ev.id}`}
                          title={ev.applied_at ? new Date(ev.applied_at).toLocaleString() : ""}
                        >
                          <span className="text-zinc-400">↳ </span>
                          <span className="line-through decoration-zinc-400/60">{ev.variant}</span>
                          {" "}
                          <span>{lang === "fr" ? "renommé par" : "renamed by"}</span>{" "}
                          <span className="font-mono text-zinc-600">{ev.applied_by || "—"}</span>
                          {" · "}
                          <span className="text-zinc-500">{_formatRelative(ev.applied_at, lang)}</span>
                          {ev.affected_count > 0 && (
                            <>
                              {" · "}
                              <span className="text-zinc-500">
                                {ev.affected_count}{" "}
                                {lang === "fr" ? "vidéo(s)" : "video(s)"}
                              </span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {showMergeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="tags-page-merge-modal">
          <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl p-5">
            <h2 className="text-base font-semibold text-zinc-900 mb-3">
              {selected.size === 1
                ? (lang === "fr" ? "Renommer ce tag" : "Rename this tag")
                : (lang === "fr" ? "Fusionner les tags sélectionnés" : "Merge selected tags")}
            </h2>
            <p className="text-xs text-zinc-600 mb-3">
              {selected.size === 1
                ? (lang === "fr"
                    ? "Tape la nouvelle version. Le tag actuel sera remplacé partout où il est utilisé."
                    : "Type the new version. The current tag will be replaced everywhere it is used.")
                : (lang === "fr"
                    ? "Choisis la version à conserver. Les autres seront remplacées sur toute l'organisation."
                    : "Choose the version to keep. The others will be replaced across the org.")}
            </p>
            <div className="flex flex-col gap-1.5 mb-4">
              {[...selected].map((tag) => (
                <label
                  key={tag}
                  className={`flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded ${
                    mergeChoice === tag ? "bg-teal-50 font-semibold text-teal-800" : "hover:bg-zinc-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="tags-page-merge-choice"
                    checked={mergeChoice === tag}
                    onChange={() => setMergeChoice(tag)}
                    data-testid={`tags-page-merge-radio-${tag}`}
                  />
                  <span>{tag}</span>
                </label>
              ))}
              <label className={`flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded ${
                ![...selected].includes(mergeChoice) ? "bg-teal-50 font-semibold text-teal-800" : "hover:bg-zinc-50"
              }`}>
                <input
                  type="radio"
                  name="tags-page-merge-choice"
                  checked={![...selected].includes(mergeChoice)}
                  onChange={() => setMergeChoice("")}
                  data-testid="tags-page-merge-radio-custom"
                />
                <input
                  type="text"
                  placeholder={lang === "fr" ? "Version personnalisée…" : "Custom version…"}
                  value={![...selected].includes(mergeChoice) ? mergeChoice : ""}
                  onChange={(e) => setMergeChoice(e.target.value)}
                  className="text-sm px-2 py-1 border border-zinc-300 rounded flex-1"
                  data-testid="tags-page-merge-custom-input"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowMergeModal(false)}
                disabled={merging}
                className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
                data-testid="tags-page-merge-cancel"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={doMerge}
                disabled={merging || !mergeChoice.trim()}
                className="text-xs font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white hover:bg-[#001f70] disabled:opacity-50"
                data-testid="tags-page-merge-confirm"
              >
                {merging ? (lang === "fr" ? "Fusion…" : "Merging…") : (lang === "fr" ? "Fusionner" : "Merge")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Tâche 1262 — Import d'une liste de tags (CSV ou Excel), sans numéro
          de vidéo. La modale d'import est celle du dictionnaire : même geste,
          même aperçu, même rollback. */}
      <CsvImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={lang === "fr" ? "Importer une liste de tags" : "Import a tag list"}
        description={
          <span>
            {tr(
              "Une colonne de tags suffit, avec ou sans ligne d'en-tête. Colonnes facultatives : ",
              "A single tag column is enough, with or without a header row. Optional columns: ",
            )}
            <code>{tr("langue", "language")}</code> (fr/en), <code>equivalent_fr</code>{" "}
            {tr("et ", "and ")}<code>{tr("nombre", "number")}</code>
            {tr(
              " (singulier/pluriel). Aucun numéro de vidéo n'est demandé : les tags entrent dans le vocabulaire de l'organisation et peuvent être posés sur les vidéos ensuite.",
              " (singular/plural). No video number is asked for: the tags enter the organisation vocabulary and can be attached to videos afterwards.",
            )}
          </span>
        }
        templateCsv={lang === "fr"
          ? "tag,langue,equivalent_fr,nombre\nREER,fr,,singulier\nRRSP,en,REER,singulier\nplacements,fr,,pluriel\n"
          : "tag,language,equivalent_fr,number\nRRSP,en,REER,singular\nREER,fr,,singular\ninvestments,en,placements,plural\n"}
        templateFilename={lang === "fr" ? "modele-import-tags.csv" : "tag-import-template.csv"}
        dataTestidPrefix="tags-import"
        lang={lang}
        onSubmit={async (payload) => {
          const { data } = await axios.post(
            `${API}/api/tags/vocabulary/import`,
            payload,
            { withCredentials: true },
          );
          if (data?.batch_id) setLastImportBatch(data.batch_id);
          await loadVocabulary();
          return data;
        }}
        resultRenderer={(res) => (
          <div className="text-xs text-zinc-700 space-y-1" data-testid="tags-import-result">
            <p>
              <strong>{res.added}</strong> tag(s) ajouté(s) au vocabulaire,{" "}
              <strong>{res.updated}</strong> mis à jour, sur {res.processed} ligne(s) lue(s).
            </p>
            {(res.errors || []).length > 0 && (
              <ul className="list-disc ml-4 text-amber-800">
                {res.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      />
    </div>
  );
}
